import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, RefreshCw, MessageSquare, Clock, User, Bot, 
  Copy, Check, ChevronLeft, ChevronRight, Zap, X, AlertCircle
} from 'lucide-react';
import { getSessions, getSessionDetails } from './adminApi';

export default function SessionExplorer({ initialSearch = '', onClearSearch }) {
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [search, setSearch] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [copiedId, setCopiedId] = useState(false);

  const fetchSessionTranscript = async (sessionId) => {
    if (!sessionId) return;
    setSelectedSessionId(sessionId);
    setLoadingDetail(true);
    setDetailError('');
    try {
      const detail = await getSessionDetails(sessionId);
      if (detail) {
        setSessionDetail(detail);
      } else {
        setDetailError('Session details not returned by server.');
      }
    } catch (err) {
      console.error('Failed to load session details:', err);
      setDetailError(err.message || 'Error loading conversation transcript');
    } finally {
      setLoadingDetail(false);
    }
  };

  const loadSessions = useCallback(async (targetPage = 1, currentSearch = search, currentStatus = statusFilter) => {
    setLoadingList(true);
    try {
      const data = await getSessions(targetPage, 15, currentSearch.trim(), currentStatus);
      const items = data.items || [];
      setSessions(items);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total || 0);
      setPage(data.page || 1);

      if (items.length > 0) {
        // Auto-select first session if none is selected or if current selection is not in list
        setSelectedSessionId((prevSelected) => {
          const exists = items.some(s => s.id === prevSelected);
          const nextSelected = exists ? prevSelected : items[0].id;
          fetchSessionTranscript(nextSelected);
          return nextSelected;
        });
      } else {
        setSelectedSessionId(null);
        setSessionDetail(null);
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setLoadingList(false);
    }
  }, [search, statusFilter]);

  // Synchronize when initialSearch prop changes (e.g. clicked from FAQ)
  useEffect(() => {
    const term = initialSearch || '';
    setSearch(term);
    loadSessions(1, term, statusFilter);
  }, [initialSearch, loadSessions, statusFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadSessions(1, search, statusFilter);
  };

  const handleClearSearch = () => {
    setSearch('');
    if (onClearSearch) onClearSearch();
    loadSessions(1, '', statusFilter);
  };

  const handleStatusChange = (newStatus) => {
    setStatusFilter(newStatus);
    loadSessions(1, search, newStatus);
  };

  const copySessionId = () => {
    if (!selectedSessionId) return;
    navigator.clipboard.writeText(selectedSessionId);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    try {
      const d = new Date(isoString);
      return d.toLocaleString([], { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (_) {
      return isoString;
    }
  };

  return (
    <div className="space-y-6">
      {/* Title & Stats */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Conversation Sessions</h2>
          <p className="text-sm text-gray-500">
            Inspect individual anonymous visitor dialogues, questions, and AI responses
          </p>
        </div>
        <div className="text-sm font-semibold text-gray-600 bg-white px-4 py-2 rounded-xl border border-gray-200">
          Total Logged Sessions: <span className="text-[#668f45] font-bold">{totalCount}</span>
        </div>
      </div>

      {/* Main 2-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[700px]">
        {/* Left Column: Session List */}
        <div className="lg:col-span-5 flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Search & Filter Bar */}
          <div className="p-4 border-b border-gray-100 space-y-3 bg-gray-50/50">
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                type="text"
                placeholder="Search by session ID, question or keyword..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-9 py-2 bg-white border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#668f45]"
              />
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              {search && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </form>

            {search && (
              <div className="flex items-center justify-between bg-[#668f45]/10 border border-[#668f45]/20 text-[#668f45] px-3 py-1.5 rounded-xl text-xs font-semibold">
                <span className="truncate pr-2">Filter: "{search}"</span>
                <button
                  onClick={handleClearSearch}
                  className="text-xs hover:underline flex-shrink-0 font-bold ml-2"
                >
                  Clear filter
                </button>
              </div>
            )}

            <div className="flex items-center justify-between gap-2">
              <div className="flex gap-1.5">
                {['', 'active', 'cleared'].map((st) => (
                  <button
                    key={st}
                    onClick={() => handleStatusChange(st)}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold capitalize transition-all ${
                      statusFilter === st
                        ? 'bg-[#668f45] text-white shadow-sm'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {st === '' ? 'All' : st}
                  </button>
                ))}
              </div>

              <button
                onClick={() => loadSessions(page, search, statusFilter)}
                title="Refresh sessions list"
                className="p-1.5 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-200/60 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Sessions List */}
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100 max-h-[560px]">
            {loadingList ? (
              <div className="p-12 text-center text-gray-400">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-[#668f45]" />
                <p className="text-xs">Loading sessions...</p>
              </div>
            ) : sessions.length === 0 ? (
              <div className="p-12 text-center text-gray-400 space-y-3">
                <MessageSquare className="w-8 h-8 mx-auto text-gray-300" />
                <p className="text-sm font-medium">No matching sessions found</p>
                {search && (
                  <button
                    onClick={handleClearSearch}
                    className="px-3 py-1.5 bg-[#668f45] text-white rounded-lg text-xs font-semibold hover:bg-[#5b803e] transition-colors"
                  >
                    Show all conversations
                  </button>
                )}
              </div>
            ) : (
              sessions.map((s) => {
                const isSelected = s.id === selectedSessionId;
                return (
                  <div
                    key={s.id}
                    onClick={() => fetchSessionTranscript(s.id)}
                    className={`p-4 cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-[#668f45]/10 border-l-4 border-[#668f45]'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="font-mono text-xs font-bold text-gray-700 truncate max-w-[200px]">
                        {s.id}
                      </span>
                      <span className={`text-[10px] uppercase font-extrabold px-2 py-0.5 rounded-full ${
                        s.status === 'cleared'
                          ? 'bg-gray-100 text-gray-500'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {s.status}
                      </span>
                    </div>

                    <p className="text-xs text-gray-800 line-clamp-2 font-medium mb-2">
                      {s.initial_question ? `"${s.initial_question}"` : 'Conversation started'}
                    </p>

                    <div className="flex items-center justify-between text-[11px] text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(s.last_activity_at || s.created_at)}
                      </span>
                      <span className="flex items-center gap-1 font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-md">
                        <MessageSquare className="w-3 h-3" />
                        {s.message_count} msgs
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination Footer */}
          {totalPages > 1 && (
            <div className="p-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-600">
              <span>Page {page} of {totalPages}</span>
              <div className="flex gap-1">
                <button
                  disabled={page <= 1}
                  onClick={() => loadSessions(page - 1, search, statusFilter)}
                  className="p-1 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => loadSessions(page + 1, search, statusFilter)}
                  className="p-1 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Conversation Transcript Inspector */}
        <div className="lg:col-span-7 bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
          {!selectedSessionId ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-center text-gray-400">
              <MessageSquare className="w-12 h-12 text-gray-300 mb-3" />
              <h3 className="font-bold text-gray-700">No session selected</h3>
              <p className="text-xs text-gray-400 mt-1">Select a session on the left to review its conversation history.</p>
            </div>
          ) : loadingDetail ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-gray-400">
              <RefreshCw className="w-8 h-8 animate-spin text-[#668f45] mb-2" />
              <p className="text-xs font-medium">Loading session transcript...</p>
            </div>
          ) : detailError ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-red-500">
              <AlertCircle className="w-10 h-10 mb-2 text-red-400" />
              <p className="font-semibold text-sm mb-3">{detailError}</p>
              <button
                onClick={() => fetchSessionTranscript(selectedSessionId)}
                className="px-4 py-2 bg-[#668f45] text-white rounded-xl text-xs font-bold hover:bg-[#5b803e] transition-colors"
              >
                Retry Loading
              </button>
            </div>
          ) : sessionDetail ? (
            <>
              {/* Transcript Header */}
              <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Session ID:</span>
                      <span className="font-mono text-xs font-bold text-gray-900 truncate">
                        {sessionDetail.id}
                      </span>
                      <button
                        onClick={copySessionId}
                        className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
                        title="Copy session ID"
                      >
                        {copiedId ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-500">
                      <span>Started: <strong>{formatDate(sessionDetail.created_at)}</strong></span>
                      <span>•</span>
                      <span>Last active: <strong>{formatDate(sessionDetail.last_activity_at)}</strong></span>
                      {sessionDetail.user_ip && (
                        <>
                          <span>•</span>
                          <span>IP: <code className="text-gray-700">{sessionDetail.user_ip}</code></span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wide ${
                      sessionDetail.status === 'cleared'
                        ? 'bg-gray-100 text-gray-600'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {sessionDetail.status}
                    </span>
                    <button
                      onClick={() => fetchSessionTranscript(sessionDetail.id)}
                      title="Refresh current chat transcript"
                      className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/50 rounded-lg transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Message Transcript Stream */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 max-h-[580px] bg-[#fbfdfc]">
                {(!sessionDetail.messages || sessionDetail.messages.length === 0) ? (
                  <p className="text-center text-sm text-gray-400 py-12">No messages recorded in this session yet.</p>
                ) : (
                  sessionDetail.messages.map((m, idx) => {
                    const isUser = m.sender === 'user';
                    return (
                      <div
                        key={idx}
                        className={`flex gap-3 max-w-[85%] ${
                          isUser ? 'ml-auto flex-row-reverse' : 'mr-auto'
                        }`}
                      >
                        {/* Avatar */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xs ${
                          isUser ? 'bg-gradient-to-tr from-[#668f45] to-[#7db34f]' : 'bg-gradient-to-tr from-gray-700 to-gray-900'
                        }`}>
                          {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                        </div>

                        {/* Message Box */}
                        <div className={`p-4 rounded-2xl shadow-sm text-sm ${
                          isUser
                            ? 'bg-gradient-to-r from-[#668f45] to-[#5b803e] text-white rounded-tr-none'
                            : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none'
                        }`}>
                          <div className="whitespace-pre-wrap leading-relaxed">
                            {m.message_text}
                          </div>

                          {/* Message metadata */}
                          <div className={`flex items-center gap-2 mt-2 pt-1 border-t text-[10px] ${
                            isUser ? 'border-white/20 text-white/80 justify-end' : 'border-gray-100 text-gray-400 justify-start'
                          }`}>
                            <span>{formatDate(m.timestamp)}</span>
                            {m.latency_ms && (
                              <span className="flex items-center gap-0.5">
                                <Zap className="w-3 h-3 text-amber-500" />
                                {m.latency_ms}ms
                              </span>
                            )}
                            {m.is_canned && (
                              <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-semibold">
                                Canned
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
